import { useState, useEffect } from 'react'
import {
  FileText,
  BarChart3,
  TrendingUp,
  Video,
} from 'lucide-react'
import { interviewerApi } from '../../lib/api/interviewer'
import { evaluationsApi } from '../../lib/api/evaluations'

interface Stats {
  totalSessions: number
  completedSessions: number
  avgRating: string
  totalEvaluations: number
}

interface MonthlyData {
  month: string
  count: number
}

export default function InterviewerReports() {
  const [stats, setStats] = useState<Stats>({
    totalSessions: 0,
    completedSessions: 0,
    avgRating: '--',
    totalEvaluations: 0,
  })
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sessionsRes, evalsRes, ratingsRes] = await Promise.all([
          interviewerApi.getSessions().catch(() => ({ data: [] })),
          evaluationsApi.getEvaluations().catch(() => ({ data: [] })),
          evaluationsApi.getMyRatings().catch(() => null),
        ])

        const sessions = sessionsRes.data || sessionsRes || []
        const evals = evalsRes.data || evalsRes || []

        setStats({
          totalSessions: sessions.length,
          completedSessions: sessions.filter(
            (s: { status: string }) => s.status === 'completed'
          ).length,
          avgRating: ratingsRes?.average || '--',
          totalEvaluations: evals.length,
        })

        // Build monthly chart data
        const months: Record<string, number> = {}
        sessions.forEach((s: { scheduled_at: string }) => {
          const date = new Date(s.scheduled_at)
          const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
          months[key] = (months[key] || 0) + 1
        })

        const sorted = Object.entries(months)
          .sort((a, b) => a[0].localeCompare(b[0]))
          .slice(-6)
          .map(([month, count]) => {
            const [y, m] = month.split('-')
            const date = new Date(parseInt(y), parseInt(m) - 1)
            return {
              month: date.toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric',
              }),
              count,
            }
          })
        setMonthlyData(sorted)
      } catch (err) {
        console.error('Failed to fetch report data:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  if (loading) {
    return <div className="loading-screen">Loading reports...</div>
  }

  const maxCount = Math.max(...monthlyData.map((d) => d.count), 1)

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Performance Reports</h1>
        <p className="page-subtitle">
          Your interview statistics and performance metrics
        </p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="icon primary">
            <Video size={24} />
          </div>
          <div className="value">{stats.totalSessions}</div>
          <div className="label">Total Sessions</div>
        </div>
        <div className="stat-card">
          <div className="icon success">
            <TrendingUp size={24} />
          </div>
          <div className="value">{stats.completedSessions}</div>
          <div className="label">Completed</div>
        </div>
        <div className="stat-card">
          <div className="icon warning">
            <BarChart3 size={24} />
          </div>
          <div className="value">{stats.avgRating}</div>
          <div className="label">Avg Rating</div>
        </div>
        <div className="stat-card">
          <div className="icon primary">
            <FileText size={24} />
          </div>
          <div className="value">{stats.totalEvaluations}</div>
          <div className="label">Evaluations</div>
        </div>
      </div>

      {/* Sessions by Month Chart */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 24 }}>
          Sessions by Month
        </h3>
        {monthlyData.length === 0 ? (
          <div className="empty-state">
            <BarChart3 size={48} />
            <h3>No data yet</h3>
            <p>Session data will appear here as you conduct interviews.</p>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 16,
              height: 200,
              paddingBottom: 32,
            }}
          >
            {monthlyData.map((d) => (
              <div
                key={d.month}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 600 }}>
                  {d.count}
                </span>
                <div
                  style={{
                    width: '100%',
                    maxWidth: 60,
                    height: `${(d.count / maxCount) * 150}px`,
                    minHeight: 8,
                    background: 'var(--primary-navy)',
                    borderRadius: '6px 6px 0 0',
                    transition: 'height 0.3s ease',
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--text-muted)',
                    textAlign: 'center',
                  }}
                >
                  {d.month}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Completion Rate */}
      <div className="card">
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
          Performance Metrics
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                Completion Rate
              </span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {stats.totalSessions > 0
                  ? Math.round(
                      (stats.completedSessions / stats.totalSessions) * 100
                    )
                  : 0}
                %
              </span>
            </div>
            <div
              style={{
                height: 8,
                background: 'var(--border)',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${stats.totalSessions > 0 ? (stats.completedSessions / stats.totalSessions) * 100 : 0}%`,
                  background: 'var(--success)',
                  borderRadius: 4,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                Evaluation Coverage
              </span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {stats.completedSessions > 0
                  ? Math.round(
                      (stats.totalEvaluations / stats.completedSessions) * 100
                    )
                  : 0}
                %
              </span>
            </div>
            <div
              style={{
                height: 8,
                background: 'var(--border)',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${stats.completedSessions > 0 ? (stats.totalEvaluations / stats.completedSessions) * 100 : 0}%`,
                  background: 'var(--primary-navy)',
                  borderRadius: 4,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
